import { useApolloClient } from '@apollo/react-hooks';
import { IDropdownOption, TextField } from '@fluentui/react';
import { ApolloError } from 'apollo-client';
import Select from 'components/controls/Select';
import React, { useCallback, useEffect, useState } from 'react';
import { StyledControlContainer } from 'shared/styled/control.styled';
import {
   StyledContainer,
   StyledPanelColumn,
   StyledPanelGrid,
   StyledPanelRow,
   StyledPanelTitle
} from 'shared/styled/panel.stayled';
import { DayFormat, MonthFormat, renderDate, renderTime, WeekdayFormat, YearFormat } from 'shared/utils/format.utils';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { OpportunityCompletion } from '../../../queries/view-opportunity.queries.types';
import { IClosureReason, IObservationCompletionLookupData } from 'shared/queries/observation-completion.queries.types';
import CompletedByDisplay from 'components/CompletedByDisplay';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { GetOpportunityCompletionLookups } from '../../../queries/opportunity-completion.queries';

interface OpportunityCompletionProps {
   observationUniqueId: string;
   opportunityCompletion: OpportunityCompletion | undefined;
   files: ObservationFileEntity[];
}

export const ViewOpportunityCompletion = ({ observationUniqueId, opportunityCompletion: nearMissCompletion, files }: OpportunityCompletionProps) => {
   const client = useApolloClient();
   const [loadingClosureReasons, setLoadingClosureReasons] = useState<boolean>(true);
   const [loadingClosureReasonsError, setLoadingClosureReasonsError] = useState<ApolloError>();
   const [closureReasons, setClosureReasons] = useState<IClosureReason[]>();

   useEffect(() => {
      loadReporterTypes();
   }, []);

   const loadReporterTypes = async () => {
      setLoadingClosureReasons(true);

      try {
         let result = await client.query<IObservationCompletionLookupData>({
            query: GetOpportunityCompletionLookups,
            variables: { request: { type: 'OPPORTUNITYCLOSUREREASON', selectedLanguage: 'GB' } },
            fetchPolicy: 'network-only'
         });
         if (result?.data?.closureReasons != null) {
            setClosureReasons(result.data.closureReasons);
         }
      } catch (e: any) {
         setLoadingClosureReasonsError(e);
      } finally {
         setLoadingClosureReasons(false);
      }
   };

   const renderCompletionDateTime = useCallback(() => {
      const datePart = renderDate(nearMissCompletion?.closeDate, {
         day: DayFormat.Numeric,
         month: MonthFormat.Short,
         weekday: WeekdayFormat.Short,
         year: YearFormat.Numeric
      });

      const timePart = renderTime(nearMissCompletion?.closeDate?.toLocaleString() ?? '00:00');

      return `${datePart} ${timePart}`;
   }, [nearMissCompletion]);

   return (
      <React.Fragment>
         <StyledContainer>
            <StyledPanelGrid fluid>
               <StyledPanelRow>
                  <StyledPanelColumn md={12} lg={6} xl={6}>
                     <StyledPanelTitle>Completion Date</StyledPanelTitle>
                     <StyledControlContainer>
                        <TextField label='' value={renderCompletionDateTime()} onChange={(e, v) => ''} disabled />
                     </StyledControlContainer>

                     <StyledPanelTitle>Completed By</StyledPanelTitle>
                     <StyledControlContainer>
                        <CompletedByDisplay completedByEmployeeId={nearMissCompletion?.employeeId} />
                     </StyledControlContainer>

                     <StyledPanelTitle>Closure Reason</StyledPanelTitle>
                     <StyledControlContainer>
                        <Select
                           required
                           selectedKey={nearMissCompletion?.closureReason}
                           multiSelect={false}
                           onChange={(e, v) => ''}
                           options={mapToDropdownOptions<IClosureReason, IDropdownOption>(
                              closureReasons,
                              reason => reason.name.toString().toUpperCase(),
                              reason => reason.description
                           )}
                           isLoading={loadingClosureReasons}
                           disabled
                           useEnglish
                        />
                     </StyledControlContainer>

                     <StyledPanelTitle>Action Taken</StyledPanelTitle>
                     <StyledControlContainer>
                        <TextField
                           label=''
                           multiline
                           value={nearMissCompletion?.actionTaken}
                           onChange={(e, v) => ''}
                           disabled
                        />
                     </StyledControlContainer>
                  </StyledPanelColumn>
               </StyledPanelRow>
            </StyledPanelGrid>
         </StyledContainer>
      </React.Fragment>
   );
};
